class ProductAddons {
  constructor(container) {
    this.container = container;
    if (!this.container || this.container._productAddons) return;

    this.inputs = Array.from(this.container.querySelectorAll('.addon-input'));
    this.mainForm = document.querySelector('form[action*="/cart/add"]');
    this.productComponent = document.querySelector('product-form-component');
    this.summary = this.container.querySelector('[data-addon-summary]');
    this.submitButton = document.querySelector('[name="add"]');
    this.isAdding = false;

    if (!this.submitButton && this.mainForm) {
      this.submitButton = this.mainForm.querySelector('button[type="submit"]');
    }

    this.basePrice = parseFloat(this.container.dataset.productBasePrice);
    if (Number.isNaN(this.basePrice)) this.basePrice = 0;

    this.container._productAddons = this;
    this.init();
  }

  init() {
    this.inputs.forEach((input) => {
      input.addEventListener('change', (event) => {
        const group = event.target.closest('.addon-group-wrapper');
        this.clearAddonError(group, true);
        this.enforceGroupRules(event.target);
        this.syncGroupState(group);
        this.calculateTotal();
        this.validateGroups();
      });
    });

    this.container.querySelectorAll('.addon-group-wrapper').forEach((group) => this.syncGroupState(group));
    this.container.querySelectorAll('.addon-error-msg').forEach((errorMsg) => {
      if (!errorMsg.dataset.defaultText) errorMsg.dataset.defaultText = errorMsg.textContent.trim();
    });
    this.calculateTotal();
    this.validateGroups();

    if (this.mainForm) {
      this.mainForm.addEventListener('submit', (event) => this.handleAddToCart(event), true);
    } else if (this.productComponent) {
      this.productComponent.addEventListener('submit', (event) => this.handleAddToCart(event), true);
    }

    if (this.submitButton) {
      this.submitButton.addEventListener('click', (event) => this.handleAddToCart(event), true);
    }
  }

  enforceGroupRules(changedInput) {
    const group = changedInput.closest('.addon-group-wrapper');
    if (!group) return;

    if (group.dataset.maxSelected === '1' && changedInput.checked) {
      group.querySelectorAll('.addon-input').forEach((input) => {
        if (input !== changedInput) input.checked = false;
      });
    }
  }

  syncGroupState(group) {
    if (!group) return;

    group.querySelectorAll('.product-addons__option').forEach((option) => {
      const input = option.querySelector('.addon-input');
      option.classList.toggle('is-selected', Boolean(input && input.checked));
    });
  }

  calculateTotal() {
    let addonTotal = 0;
    let addonCompareTotal = 0;

    this.inputs.forEach((input) => {
      if (input.checked && input.value !== '') {
        const price = parseFloat(input.dataset.price) || 0;
        const comparePrice = parseFloat(input.dataset.comparePrice) || price;

        addonTotal += price;
        addonCompareTotal += comparePrice;
      }
    });

    this.updateSummary(addonTotal);

    const productPrice = this.basePrice;
    let productComparePrice = parseFloat(this.container.dataset.productComparePrice);

    if (!productComparePrice || Number.isNaN(productComparePrice)) {
      const stickyBar = document.querySelector('[data-sticky-bar]');
      productComparePrice = stickyBar
        ? parseFloat(stickyBar.dataset.productComparePrice) || this.basePrice
        : this.basePrice;
    }

    document.dispatchEvent(
      new CustomEvent('addon-price-updated', {
        detail: {
          productPrice,
          productComparePrice,
          addonTotal,
          addonCompareTotal,
          currentTotal: productPrice + addonTotal,
          compareTotal: productComparePrice + addonCompareTotal,
        },
      })
    );
  }

  updateSummary(addonTotal) {
    if (!this.summary) return;

    if (addonTotal > 0) {
      this.summary.hidden = false;
      this.summary.textContent = 'Selected accessories: +$' + (addonTotal / 100).toFixed(2);
    } else {
      this.summary.textContent = '';
      this.summary.hidden = true;
    }
  }

  validateGroups() {
    let isValid = true;

    this.container.querySelectorAll('.addon-group-wrapper').forEach((group) => {
      const errorMsg = group.querySelector('.addon-error-msg');
      this.clearAddonError(group);

      if (group.dataset.required !== 'true') return;

      const hasSelection = Array.from(group.querySelectorAll('.addon-input')).some((input) => input.checked);
      if (!hasSelection) {
        isValid = false;
        if (errorMsg) {
          errorMsg.textContent = errorMsg.dataset.defaultText || 'Please choose an option to continue.';
          errorMsg.dataset.addonErrorType = 'validation';
          errorMsg.classList.add('is-visible');
        }
      }
    });

    this.syncSubmitButtonState(isValid);
    return isValid;
  }

  clearAddonError(group, includeServer = false) {
    const errorMsg = group?.querySelector('.addon-error-msg');
    if (!errorMsg) return;

    if (!errorMsg.dataset.defaultText) errorMsg.dataset.defaultText = errorMsg.textContent.trim();
    if (!includeServer && errorMsg.dataset.addonErrorType === 'server') return;

    errorMsg.classList.remove('is-visible');
    errorMsg.textContent = errorMsg.dataset.defaultText || 'Please choose an option to continue.';
    delete errorMsg.dataset.addonErrorType;
  }

  syncSubmitButtonState(isValid = this.validateGroups()) {
    if (!this.submitButton) return;

    const productCanAdd = this.submitButton.dataset.canAddToCart !== 'false';
    this.submitButton.disabled = this.isAdding || !productCanAdd || !isValid;

    if (this.submitButton.disabled) {
      this.submitButton.setAttribute('aria-disabled', 'true');
    } else {
      this.submitButton.removeAttribute('aria-disabled');
    }
  }

  showAddonError(message, inputs = []) {
    const affectedGroup = inputs[0]?.closest('.addon-group-wrapper') || this.container.querySelector('.addon-group-wrapper');
    const errorMsg = affectedGroup?.querySelector('.addon-error-msg') || this.container.querySelector('.addon-error-msg');

    if (errorMsg) {
      errorMsg.textContent = message;
      errorMsg.dataset.addonErrorType = 'server';
      errorMsg.classList.add('is-visible');
    }
  }

  markAddonsUnavailable(inputs, message) {
    inputs.forEach((input) => {
      input.checked = false;
      input.disabled = true;
      input.setAttribute('aria-disabled', 'true');

      const option = input.closest('.product-addons__option');
      option?.classList.remove('is-selected');
      option?.classList.add('is-disabled');
      option?.setAttribute('aria-disabled', 'true');

      const price = option?.querySelector('.product-addons__price > span:first-child');
      if (price) price.textContent = 'Unavailable';
    });

    this.container.querySelectorAll('.addon-group-wrapper').forEach((group) => this.syncGroupState(group));
    this.calculateTotal();
    this.validateGroups();
    this.showAddonError(message, inputs);
  }

  getSelectedAddons() {
    return this.inputs.filter((input) => input.checked && !input.disabled && input.value !== '');
  }

  getCartSectionIds() {
    const sectionIds = new Set();
    document.querySelectorAll('cart-items-component').forEach((component) => {
      if (component.dataset.sectionId) sectionIds.add(component.dataset.sectionId);
    });
    return Array.from(sectionIds);
  }

  setButtonLoading(isLoading) {
    if (!this.submitButton) return;

    const label = this.submitButton.querySelector('.add-to-cart-text__content');
    if (label) {
      if (!label.dataset.originalText) label.dataset.originalText = label.textContent.trim();
      label.textContent = isLoading ? 'Adding...' : label.dataset.originalText;
    }

    this.isAdding = isLoading;
    this.syncSubmitButtonState();
  }

  getMainVariantId(formData) {
    const formVariantId = formData.get('id');
    if (formVariantId) return formVariantId;

    const formInput = this.mainForm?.querySelector('[name="id"]:not([disabled])');
    if (formInput && formInput.value) return formInput.value;

    const pageInput = document.querySelector('product-form-component [name="id"]:not([disabled])');
    if (pageInput && pageInput.value) return pageInput.value;

    return '';
  }

  getLineItemProperties(formData) {
    const properties = {};

    formData.forEach((value, key) => {
      const match = key.match(/^properties\[(.+)\]$/);
      if (!match || value === '') return;
      properties[match[1]] = value;
    });

    return properties;
  }

  buildCartItems(formData, selectedAddons) {
    const mainVariantId = this.getMainVariantId(formData);
    if (!mainVariantId) {
      throw new Error('Please choose an available product option.');
    }

    const mainQuantity = Number(formData.get('quantity')) || Number(this.productComponent?.dataset.quantityDefault) || 1;
    const mainItem = {
      id: Number(mainVariantId),
      quantity: mainQuantity,
    };

    const sellingPlan = formData.get('selling_plan');
    if (sellingPlan) mainItem.selling_plan = Number(sellingPlan);

    const properties = this.getLineItemProperties(formData);
    if (Object.keys(properties).length > 0) mainItem.properties = properties;

    const items = [mainItem];
    selectedAddons.forEach((input) => {
      items.push({
        id: Number(input.value),
        quantity: 1,
      });
    });

    return items;
  }

  dispatchCartUpdate(response, mainVariantId, itemCount) {
    document.dispatchEvent(
      new CustomEvent('cart:update', {
        bubbles: true,
        detail: {
          resource: response,
          sourceId: String(mainVariantId),
          data: {
            source: 'product-form-component',
            itemCount,
            variantId: String(mainVariantId),
            productId: this.productComponent ? this.productComponent.dataset.productId : undefined,
            sections: response.sections || {},
          },
        },
      })
    );
  }

  async handleAddToCart(event) {
    if (this.isAdding) {
      event.preventDefault();
      event.stopImmediatePropagation();
      return;
    }

    if (!this.validateGroups()) {
      event.preventDefault();
      event.stopImmediatePropagation();
      return;
    }

    const selectedAddons = this.getSelectedAddons();
    if (selectedAddons.length === 0) return;

    event.preventDefault();
    event.stopImmediatePropagation();

    const formData = this.mainForm ? new FormData(this.mainForm) : new FormData();
    let items;
    let mainVariantId;

    try {
      items = this.buildCartItems(formData, selectedAddons);
      mainVariantId = String(items[0].id);
    } catch (error) {
      alert(error.message || 'Please choose an available product option.');
      return;
    }

    const sections = this.getCartSectionIds();
    const payload = { items };
    if (sections.length > 0) {
      payload.sections = sections.join(',');
      payload.sections_url = window.location.pathname;
    }

    this.setButtonLoading(true);

    try {
      const response = await fetch(window.Shopify.routes.root + 'cart/add.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
        body: JSON.stringify(payload),
      });

      const data = await response.json();
      if (!response.ok) {
        const message = data.description || data.message || 'Unable to add accessories';
        if (/sold out|unavailable/i.test(message)) {
          this.markAddonsUnavailable(selectedAddons, message);
          return;
        }
        throw new Error(message);
      }

      const addedItemCount = items.reduce((total, item) => total + item.quantity, 0);
      this.dispatchCartUpdate(data, mainVariantId, addedItemCount);
    } catch (error) {
      console.error(error);
      this.showAddonError(error.message || 'An error occurred. Please try again.', selectedAddons);
    } finally {
      this.setButtonLoading(false);
    }
  }
}

function initProductAddons() {
  document.querySelectorAll('[data-product-addons]').forEach((container) => new ProductAddons(container));
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initProductAddons, { once: true });
} else {
  initProductAddons();
}

window.requestAnimationFrame(initProductAddons);
window.setTimeout(initProductAddons, 100);
window.setTimeout(initProductAddons, 1000);
document.addEventListener('shopify:section:load', initProductAddons);
document.addEventListener('variant:update', initProductAddons);

let productAddonsAttempts = 0;
const productAddonsRetry = window.setInterval(() => {
  initProductAddons();
  productAddonsAttempts += 1;

  const containers = Array.from(document.querySelectorAll('[data-product-addons]'));
  const initialized = containers.length > 0 && containers.every((container) => container._productAddons);
  if (initialized || productAddonsAttempts >= 40) {
    window.clearInterval(productAddonsRetry);
  }
}, 250);

const productAddonsObserverTarget = document.documentElement || document.body;
if (productAddonsObserverTarget) {
  const productAddonsObserver = new MutationObserver(() => initProductAddons());
  productAddonsObserver.observe(productAddonsObserverTarget, { childList: true, subtree: true });
}
